/**
 * Module dependencies.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from 'app/lib/utils';
import { MenuItem } from 'app/types/navigation';

/**
 * Shared row styles.
 */

const rowBase =
  'group flex w-full items-center gap-2.5 rounded-md py-2 pr-2.5 text-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring [&_svg]:h-[17px] [&_svg]:w-[17px] [&_svg]:shrink-0';

// On the muted sidebar, hover/active use a white (bg-card) pill so they stand out.
const rowIdle =
  'font-medium text-muted-foreground hover:bg-card/60 hover:text-foreground [&_svg]:text-muted-foreground';

// `hover:*` variants keep the active row stable — otherwise the global antd
// `a:hover` rule repaints it blue on hover.
const rowActive =
  'bg-card font-semibold text-foreground shadow-sm hover:bg-card hover:text-foreground [&_svg]:text-foreground';

/**
 * Path match helper — exact or as a route prefix.
 */

export function matchPath(pathname: string, path?: string): boolean {
  if (!path) {
    return false;
  }

  return pathname === path || pathname.startsWith(path.endsWith('/') ? path : `${path}/`);
}

/**
 * Whether any node in the subtree matches the current path.
 */

function subtreeMatches(item: MenuItem, pathname: string): boolean {
  if (matchPath(pathname, item.pathname)) {
    return true;
  }

  return (item.children ?? []).some(child => subtreeMatches(child, pathname));
}

/**
 * `Props` type.
 */

type Props = {
  item: MenuItem;
  activeKey?: string;
  pathname: string;
  level?: number;
  onNavigate: () => void;
};

/**
 * Export `NavItem` component.
 */

export function NavItem({ item, activeKey, pathname, level = 0, onNavigate }: Props): JSX.Element {
  const hasChildren = !!item.children?.length;
  const indent = { paddingLeft: 10 + level * 15 } as React.CSSProperties;
  const containsActive = hasChildren && subtreeMatches(item, pathname);
  const [open, setOpen] = useState(containsActive);

  useEffect(() => {
    if (containsActive) {
      setOpen(true);
    }
  }, [containsActive]);

  if (!hasChildren) {
    const active = item.key === activeKey;

    return (
      <Link
        className={cn(rowBase, active ? rowActive : rowIdle)}
        onClick={onNavigate}
        style={indent}
        to={item.pathname as string}
      >
        <span className={'flex items-center'}>{item.icon}</span>

        <span className={'min-w-0 flex-1 truncate'}>{item.label}</span>
      </Link>
    );
  }

  return (
    <div>
      <button className={cn(rowBase, rowIdle)} onClick={() => setOpen(value => !value)} style={indent} type={'button'}>
        <span className={'flex items-center'}>{item.icon}</span>

        <span className={'min-w-0 flex-1 truncate text-left'}>{item.label}</span>

        <ChevronRight className={cn('h-3.5 w-3.5 transition-transform duration-200', open && 'rotate-90')} />
      </button>

      {open && (
        <div className={'mt-0.5 space-y-0.5'}>
          {item.children?.map(child => (
            <NavItem
              activeKey={activeKey}
              item={child}
              key={child.key}
              level={level + 1}
              onNavigate={onNavigate}
              pathname={pathname}
            />
          ))}
        </div>
      )}
    </div>
  );
}
